import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { useEmployeesByAccessRoleQuery } from '@/modules/employees/application/employeeQueries';
import { useQualitySettings } from '@/shared/hooks/useQualitySettings';
import { ClockDisplay } from '../components/ClockDisplay';
import { ActionButton } from '../components/ActionButton';
import { AccessButton } from '../components/AccessButton';

const EnterIcon = ({ s = 24 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const ExitIcon = ({ s = 24 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const UserIcon = ({ s = 17 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const AdminIcon = ({ s = 17 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export function HomePage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { selectedRole } = useProfile();

  // side-effect: seeds db.employees + db.faceEmbeddings on terminal entry
  useEmployeesByAccessRoleQuery(selectedRole?.idAccessRole ?? null);

  const { homeButtonAuto } = useQualitySettings();

  const handleNavigate = (action: string) => {
    void navigate(`/scan/${action}`);
  };

  const handleAuto = () => {
    void navigate('/scan/automatic');
  };

  return (
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '30px 30px 26px',
          overflow: 'hidden',
        }}
      >
        <div
          className="mi-anim-fade-in"
          style={{
            flex: 1,
            width: '100%',
            maxWidth: 680,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Clock */}
          <ClockDisplay T={T} />

          {/* Divider */}
          <div style={{ width: '100%', height: 1, background: T.border }} />

          {/* Marking action buttons */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 15, marginTop: -18 }}>
            {/* Prompt — letterSpacing normal (matchea mockup medido 2026-07-16).
                Sin override hereda -0.02em global de `index.css` y queda más
                comprimido que el mockup. */}
            <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 500, textAlign: 'center', letterSpacing: 'normal' }}>
              {t('home.markingPrompt')}
            </p>
            {homeButtonAuto ? (
              /* Modo "Botón único" canónico del mockup (medido 2026-07-16):
                 UN botón verde full-width, layout horizontal, texto "Marcar /
                 Registro de jornada". Reemplaza los 2 botones Entrada/Salida
                 — el sistema decide internamente el tipo de marcaje. */
              <ActionButton
                label="Marcar"
                sublabel="Registro de jornada"
                color={C.success}
                icon={<EnterIcon s={30} />}
                onClick={handleAuto}
                T={T}
                layout="row"
              />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, width: '100%' }}>
                <ActionButton
                  label={t('home.markEntry')}
                  sublabel={t('home.entrySubtitle')}
                  color={C.success}
                  icon={<EnterIcon s={24} />}
                  onClick={() => { handleNavigate('entrada'); }}
                  T={T}
                />
                <ActionButton
                  label={t('home.markExit')}
                  sublabel={t('home.exitSubtitle')}
                  color={C.danger}
                  icon={<ExitIcon s={24} />}
                  onClick={() => { handleNavigate('salida'); }}
                  T={T}
                />
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ width: '100%', height: 1, background: T.border }} />

          {/* Access section */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textAlign: 'center', letterSpacing: '.02em', marginBottom: 10 }}>
              {t('home.systemAccess')}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <AccessButton
                label={t('home.employeeAccess')}
                color={C.indigo}
                icon={<UserIcon s={17} />}
                onClick={() => { handleNavigate('acceso-empleado'); }}
              />
              <AccessButton
                label={t('home.adminAccess')}
                color={C.blue}
                icon={<AdminIcon s={17} />}
                onClick={() => { handleNavigate('acceso-admin'); }}
              />
            </div>
          </div>

          {/* Footer version */}
          <div style={{ fontSize: 9, color: T.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {t('home.version')}
            <span style={{ fontSize: 10 }}>©</span>
          </div>
        </div>
      </main>
  );
}
